import { Fragment, useMemo, useState } from "react";
import { Dialog, Disclosure, Menu, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  FunnelIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";

import { singleFilter, sortOptions } from "./FilterData";
import api from "../../../../config/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { findProducts } from "../../../../Redux/Customers/Product/Action";
import { Button, TextField, Box, Slider } from "@mui/material";
import HomeProductCard from "../../Home/HomeProductCard";
import { ProductGridSkeleton } from "../../../../components/Skeleton";
import { usePageTitle } from "../../../../utils/pageTitle";

// Price is a range slider (not an attribute); Color & Size options are loaded
// live from the admin-managed attributes.
const PRICE_FILTER = { id: "price", name: "Price", options: [] };

// Top of the price slider. A slider left at either end means "no limit" there,
// so products priced above it still show. The first load used to send a
// hardcoded maxPrice of 10000, which hid every product over ₹10,000 until a
// filter was applied.
const PRICE_MAX = 50000;
const priceParams = ([min, max]) => ({
  minPrice: min > 0 ? min : "",
  maxPrice: max < PRICE_MAX ? max : "",
});

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const prettify = (s = "") =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Product() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const param = useParams();
  const { customersProduct } = useSelector((store) => store);
  const location = useLocation();
  const intialFilters = {
    color: [],
    fabric: [],
    brand: [],
    size: [],
    price: [],
    disccout: [],
    rating: [],
    // In Stock is selected by default; unticking it shows every product.
    stock: ["in_stock"],
  };
  const [selectedFilters, setSelectedFilters] = useState({ ...intialFilters });
  const [selectedPageNo, setSelectedPageNo] = useState(1);
  const [priceRange, setPriceRange] = useState([0, PRICE_MAX]);
  // Newest first by default — must match the two hardcoded `sort` values in the
  // initial-load and reset-filters dispatches below, or the dropdown would show
  // one order while the list is fetched in another.
  const [selectedSort, setSelectedSort] = useState("newest");
  const [selectedFilteresPagination, setSelectedFilteresPagination] = useState({
    color: [],
    fabric: [],
    brand: [],
    size: [],
    price: [],
    disccout: [],
    rating: [],
    stock: ["in_stock"],
  });

  // Filter options are the admin-managed attributes, intersected with what is
  // actually present in THIS category (facets) — so the sidebar only offers
  // colours/fabrics/sizes that return results here (category-aware filters).
  const [attrData, setAttrData] = useState({ colors: [], fabrics: [], sizes: [] });
  const [facets, setFacets] = useState(null); // null until loaded → show all

  useEffect(() => {
    api
      .get("/api/attributes")
      .then(({ data }) => setAttrData(data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const cat = param.lavelThree;
    setFacets(null);
    if (!cat) return;
    api
      .get(`/api/products/facets?category=${encodeURIComponent(cat)}`)
      .then(({ data }) => setFacets(data || { colors: [], fabrics: [], sizes: [] }))
      .catch(() => setFacets(null));
  }, [param.lavelThree]);

  const dynamicFilters = useMemo(() => {
    // While facets are loading (null) show everything; once loaded, keep only
    // options that exist in this category (case-insensitive).
    const present = (list, val) =>
      !facets || (list || []).some((x) => x.toLowerCase() === String(val).toLowerCase());
    const built = [];
    const colorOpts = (attrData.colors || [])
      .filter((c) => present(facets?.colors, c.name))
      .map((c) => ({ value: c.name, label: c.name, hex: c.hex }));
    const fabricOpts = (attrData.fabrics || [])
      .filter((f) => present(facets?.fabrics, f))
      .map((f) => ({ value: f, label: f }));
    const brandOpts = (attrData.brands || [])
      .filter((b) => present(facets?.brands, b))
      .map((b) => ({ value: b, label: b }));
    const sizeOpts = (attrData.sizes || [])
      .filter((s) => present(facets?.sizes, s))
      .map((s) => ({ value: s, label: s }));
    if (colorOpts.length) built.push({ id: "color", name: "Color", options: colorOpts });
    if (fabricOpts.length) built.push({ id: "fabric", name: "Fabric", options: fabricOpts });
    // Brand only when there's more than one to choose from (hidden with a single brand).
    if (brandOpts.length > 1) built.push({ id: "brand", name: "Brand", options: brandOpts });
    if (sizeOpts.length) built.push({ id: "size", name: "Size", options: sizeOpts });
    built.push(PRICE_FILTER);
    return built;
  }, [attrData, facets]);

  const handleSliderChange = (e, newValue) => setPriceRange(newValue);

  // Paging, sorting and filtering replace the grid WITHOUT navigating, so the
  // router-level ScrollToTop never sees them — clicking page 2 while at the
  // bottom left you looking at the footer of a brand new result set.
  const scrollToTop = () => window.scrollTo(0, 0);

  const decodedQueryString = decodeURIComponent(location.search);
  const searchParams = new URLSearchParams(decodedQueryString);

  const handleSortChange = (value) => {
    setSelectedSort(value);
    scrollToTop();
    dispatch(
      findProducts({
        category: query,
        view_all: view_all,
        colors: selectedFilters.color,
        fabrics: selectedFilters.fabric,
        brands: selectedFilters.brand,
        sizes: selectedFilters.size,
        ...priceParams(priceRange),
        minDiscount: selectedFilters.disccout.length ? Number(selectedFilters.disccout[0]) : 0,
        minRating: selectedFilters.rating.length ? Number(selectedFilters.rating[0]) : 0,
        sort: value,
        pageNumber: 1,
        pageSize: 24,
        stock: selectedFilters.stock[0] || "",
      })
    );
  };

  const handlePaginationChange = (event, value) => {
    setSelectedPageNo(value);
    scrollToTop();
    dispatch(
      findProducts({
        category: query,
        view_all: view_all,
        colors: selectedFilteresPagination.color,
        fabrics: selectedFilteresPagination.fabric,
        brands: selectedFilteresPagination.brand,
        sizes: selectedFilteresPagination.size,
        ...priceParams(priceRange),
        minDiscount: selectedFilteresPagination.disccout.length ? Number(selectedFilteresPagination.disccout[0]) : 0,
        minRating: selectedFilteresPagination.rating.length ? Number(selectedFilteresPagination.rating[0]) : 0,
        sort: selectedSort,
        pageNumber: value,
        pageSize: 24,
        stock: selectedFilteresPagination.stock[0] || "",
      })
    );
  };

  let view_all = searchParams.get("view_all");
  const query = param.lavelThree;
  usePageTitle(prettify(query));

  // Drill into another category — used by the sidebar sub-category list and the
  // breadcrumb links. Mirrors the navbar's category navigation.
  const goToCategory = (name) =>
    navigate(`/${name}/${name}/${name}?view_all=true`);

  useEffect(() => {
    // Reset filters on category change so the sidebar matches the new results.
    setSelectedFilters({ ...intialFilters });
    setSelectedFilteresPagination({
      color: [], fabric: [], brand: [], size: [], price: [], disccout: [], rating: [], stock: ["in_stock"],
    });
    setSelectedPageNo(1);
    dispatch(
      findProducts({
        category: query,
        view_all: view_all,
        colors: [],
        sizes: [],
        ...priceParams([0, PRICE_MAX]),
        minDiscount: 0,
        sort: "newest",
        pageNumber: 1,
        pageSize: 24,
        stock: "in_stock",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.lavelThree]);

  let newSelectedFilteres = Object.assign({}, selectedFilters);

  const handleFilter = (value, sectionId) => {
    if (sectionId === "stock" || sectionId === "disccout" || sectionId === "rating") {
      // single-select sections: toggle off if the same value is reclicked
      newSelectedFilteres[sectionId] =
        newSelectedFilteres[sectionId][0] === value ? [] : [value];
      setSelectedFilters(newSelectedFilteres);
      return;
    }
    if (!newSelectedFilteres[sectionId].includes(value)) {
      newSelectedFilteres[sectionId].push(value);
      setSelectedFilters(newSelectedFilteres);
    } else if (newSelectedFilteres[sectionId].includes(value)) {
      newSelectedFilteres = {
        ...newSelectedFilteres,
        [sectionId]: selectedFilters[sectionId].filter((item) => item !== value),
      };
      setSelectedFilters(newSelectedFilteres);
    }
  };

  const handleApplyFilter = () => {
    scrollToTop();
    dispatch(
      findProducts({
        category: query,
        view_all: view_all,
        colors: selectedFilters.color,
        fabrics: selectedFilters.fabric,
        brands: selectedFilters.brand,
        sizes: selectedFilters.size,
        ...priceParams(priceRange),
        minDiscount: selectedFilters.disccout.length ? Number(selectedFilters.disccout[0]) : 0,
        minRating: selectedFilters.rating.length ? Number(selectedFilters.rating[0]) : 0,
        sort: selectedSort,
        pageNumber: 1,
        pageSize: 24,
        stock: selectedFilters.stock[0] || "",
      })
    );
    setMobileFiltersOpen(false);
    setSelectedFilteresPagination(JSON.parse(JSON.stringify(selectedFilters)));
  };

  const resetFiltersHanlder = () => {
    scrollToTop();
    dispatch(
      findProducts({
        category: query,
        view_all: view_all,
        colors: [],
        sizes: [],
        ...priceParams([0, PRICE_MAX]),
        minDiscount: 0,
        sort: "newest",
        pageNumber: 1,
        pageSize: 24,
        stock: "in_stock",
      })
    );
    setSelectedFilters({ ...intialFilters, stock: ["in_stock"] });
    setSelectedFilteresPagination({ ...intialFilters, stock: ["in_stock"] });
    setPriceRange([0, PRICE_MAX]);
    setMobileFiltersOpen(false);
  };

  const products = customersProduct?.products?.[`${query}`]?.content || [];
  const totalPages = customersProduct?.products?.[`${query}`]?.totalPages || 0;
  const totalProducts = customersProduct?.products?.[`${query}`]?.total || 0;
  const isEmpty = products.length <= 0 && !customersProduct?.loading;

  // "Showing X–Y of Z" range for the current page (24 per page).
  const PAGE_SIZE = 24;
  const rangeStart = totalProducts === 0 ? 0 : (selectedPageNo - 1) * PAGE_SIZE + 1;
  const rangeEnd = (selectedPageNo - 1) * PAGE_SIZE + products.length;

  // shared filter body (used in sidebar + mobile drawer)
  // Render FUNCTIONS, not components. These used to be declared here and
  // rendered as <FilterBody />, but a function declared inside a component gets
  // a new identity on every render, and React compares element types by
  // reference — so the entire filter panel was unmounted and remounted on each
  // keystroke or drag. That destroyed the Slider mid-gesture, which is why the
  // price slider could not be dragged at all. Calling them inlines the JSX into
  // this component's own tree, so nothing remounts.
  const renderFilterActions = () => (
    <div className="flex gap-3">
      <Button variant="outlined" onClick={resetFiltersHanlder}
        sx={{ flex: 1, borderColor: "rgba(94,62,62,0.3)", color: "#5e3e3e" }}>
        Reset
      </Button>
      <Button variant="contained" onClick={handleApplyFilter} sx={{ flex: 1 }}>
        Apply
      </Button>
    </div>
  );

  // showActions=false on mobile: the drawer renders the buttons in a pinned
  // footer instead, so they stay reachable without scrolling past every filter.
  const renderFilterBody = ({ showActions = true } = {}) => (
    <>
      {/* Category drill-down — sub-categories of the current one that have
          products. Clicking narrows into that sub-category. */}
      {facets?.subcategories?.length > 0 && (
        <Disclosure as="div" defaultOpen className="border-b border-[#eadcc0] py-4">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full items-center justify-between text-sm">
                <span className="font-semibold text-[#511d00]">Category</span>
                <span className="text-[#b28585]">
                  {open ? <MinusIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                </span>
              </Disclosure.Button>
              <Disclosure.Panel className="pt-4">
                <div className="space-y-2.5">
                  {facets.subcategories.map((sc) => (
                    <button
                      key={sc.name}
                      type="button"
                      onClick={() => goToCategory(sc.name)}
                      className="flex w-full items-center gap-1 text-left text-sm text-[#5e3e3e] transition-colors hover:text-[#b28585]"
                    >
                      <ChevronDownIcon className="h-4 w-4 -rotate-90 text-[#c9b0b0]" />
                      {prettify(sc.title || sc.name)}
                    </button>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}

      {dynamicFilters.map((section) => (
        <Disclosure as="div" key={section.id} defaultOpen className="border-b border-[#eadcc0] py-4">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full items-center justify-between text-sm">
                <span className="font-semibold text-[#511d00]">{section.name}</span>
                <span className="text-[#b28585]">
                  {open ? <MinusIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                </span>
              </Disclosure.Button>
              <Disclosure.Panel className="pt-4">
                {section.id === "price" ? (
                  <Box>
                    <Slider
                      value={priceRange}
                      onChange={handleSliderChange}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{ color: "#b28585" }}
                      min={0}
                      max={PRICE_MAX}
                    />
                    <div className="flex gap-2">
                      <TextField name="min" variant="outlined" size="small"
                        value={priceRange[0]}
                        onChange={(e) => setPriceRange([e.target.value, priceRange[1]])} />
                      <TextField hiddenLabel variant="outlined" size="small"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], e.target.value])} />
                    </div>
                  </Box>
                ) : (
                  <div className="space-y-3">
                    {section.options.map((option, optionIdx) => (
                      <label key={option.value} className="flex cursor-pointer items-center text-sm text-[#5e3e3e]">
                        <input
                          id={`filter-${section.id}-${optionIdx}`}
                          name={`${section.id}[]`}
                          defaultValue={option.value}
                          type="checkbox"
                          checked={newSelectedFilteres[section.id].includes(option.value)}
                          className="mr-3 h-4 w-4 cursor-pointer rounded border-[#cbb] accent-[#b28585]"
                          onChange={() => handleFilter(option.value, section.id)}
                        />
                        {option.hex && (
                          <span
                            className="mr-2 inline-block h-3.5 w-3.5 rounded-full border border-black/15"
                            style={{ backgroundColor: option.hex }}
                          />
                        )}
                        {option.label}
                      </label>
                    ))}
                  </div>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      ))}

      {singleFilter.map((section) => (
        <Disclosure as="div" key={section.id} defaultOpen className="border-b border-[#eadcc0] py-4">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full items-center justify-between text-sm">
                <span className="font-semibold text-[#511d00]">{section.name}</span>
                <span className="text-[#b28585]">
                  {open ? <MinusIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                </span>
              </Disclosure.Button>
              <Disclosure.Panel className="pt-4">
                <div className="space-y-3">
                  {section.options.map((option, optionIdx) => (
                    <label key={option.value} className="flex cursor-pointer items-center text-sm text-[#5e3e3e]">
                      <input
                        name={section.id}
                        value={option.value}
                        type="radio"
                        checked={selectedFilters[section.id].includes(option.value)}
                        onChange={(e) => handleFilter(e.target.value, section.id)}
                        className="mr-3 h-4 w-4 cursor-pointer accent-[#b28585]"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      ))}

      {/* Rating — appears only once the category has reviewed products. */}
      {facets?.hasRatings && (
        <Disclosure as="div" defaultOpen className="border-b border-[#eadcc0] py-4">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full items-center justify-between text-sm">
                <span className="font-semibold text-[#511d00]">Rating</span>
                <span className="text-[#b28585]">
                  {open ? <MinusIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                </span>
              </Disclosure.Button>
              <Disclosure.Panel className="pt-4">
                <div className="space-y-3">
                  {[4, 3, 2].map((r) => (
                    <label key={r} className="flex cursor-pointer items-center text-sm text-[#5e3e3e]">
                      <input
                        name="rating"
                        value={String(r)}
                        type="radio"
                        checked={selectedFilters.rating.includes(String(r))}
                        onChange={(e) => handleFilter(e.target.value, "rating")}
                        className="mr-3 h-4 w-4 cursor-pointer accent-[#b28585]"
                      />
                      {r}★ &amp; up
                    </label>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}

      {showActions && (
        <div className="pt-5">
          {renderFilterActions()}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[#fffbf2]">
      {/* Mobile filter dialog */}
      <Transition.Root show={mobileFiltersOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileFiltersOpen}>
          <Transition.Child as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 z-40 flex">
            <Transition.Child as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="translate-x-full">
              {/* Three rows: fixed header, the ONLY scrolling area, pinned
                  actions. The panel itself no longer scrolls — previously the
                  whole drawer was one scroll box, so Reset/Apply sat below
                  every filter section and were off-screen until you scrolled
                  to the very bottom. */}
              <Dialog.Panel className="ml-auto flex h-full w-full max-w-xs flex-col bg-[#FFFBF2] shadow-xl">
                <div className="flex shrink-0 items-center justify-between px-5 py-4">
                  <h2 className="font-display text-2xl font-bold text-[#511d00]">Filters</h2>
                  <button type="button" className="p-2 text-[#5e3e3e]" onClick={() => setMobileFiltersOpen(false)}>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <form className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
                  {renderFilterBody({ showActions: false })}
                </form>
                {/* pb accounts for the iOS home-indicator inset. */}
                <div className="shrink-0 border-t border-[#eadcc0] bg-[#FFFBF2] px-5 pt-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(42,24,16,0.06)]">
                  {renderFilterActions()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <main className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
        {/* Header */}
        <div className="mb-7">
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.22em] text-[#b28585]">
            {(facets?.ancestry?.length
              ? facets.ancestry
              : [...new Set([param.lavelOne, param.lavelTwo].filter(Boolean))].map(
                  (n) => ({ name: n, title: n })
                )
            ).map((a, i) => (
              <Fragment key={a.name + i}>
                {i > 0 && <span className="mx-1.5 text-[#d6b9b9]">›</span>}
                <button
                  type="button"
                  onClick={() => goToCategory(a.name)}
                  className="transition-colors hover:text-[#5e3e3e]"
                >
                  {prettify(a.title || a.name)}
                </button>
              </Fragment>
            ))}
          </p>
          <div className="flex items-end justify-between border-b border-[#eadcc0] pb-5">
            <div>
              <h1 className="font-display text-4xl font-bold capitalize text-[#511d00]">
                {prettify(query) || "Products"}
              </h1>
              {totalProducts > 0 && (
                <p className="mt-1 text-sm text-[#8a7575]">
                  Showing {rangeStart}–{rangeEnd} of {totalProducts} product
                  {totalProducts === 1 ? "" : "s"}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="group inline-flex items-center gap-1 rounded-full border border-[#eadcc0] bg-white px-4 py-2 text-sm font-semibold text-[#5e3e3e] hover:border-[#b28585]">
                  Sort
                  <ChevronDownIcon className="h-4 w-4 text-[#b28585]" />
                </Menu.Button>
                <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-44 origin-top-right overflow-hidden rounded-xl border border-[#eadcc0] bg-white shadow-xl focus:outline-none">
                    {sortOptions.map((option) => (
                      <Menu.Item key={option.name}>
                        {({ active }) => (
                          <p
                            onClick={() => handleSortChange(option.query)}
                            className={classNames(
                              active ? "bg-[#f8ecd7] text-[#511d00]" : "text-[#5e3e3e]",
                              "cursor-pointer px-4 py-2.5 text-sm"
                            )}
                          >
                            {option.name}
                          </p>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
              <button
                type="button"
                className="flex items-center gap-1 rounded-full border border-[#eadcc0] bg-white px-4 py-2 text-sm font-semibold text-[#5e3e3e] hover:border-[#b28585] lg:hidden"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <FunnelIcon className="h-4 w-4 text-[#b28585]" /> Filters
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-24 rounded-2xl border border-[#eadcc0] bg-white p-5 shadow-sm">
              <h2 className="mb-1 font-semibold text-[#511d00]">Filters</h2>
              <form>
                {renderFilterBody()}
              </form>
            </div>
          </aside>

          {/* Grid / skeleton / empty */}
          <div className="min-w-0 flex-1">
            {customersProduct?.loading ? (
              <ProductGridSkeleton count={12} />
            ) : isEmpty ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-[#eadcc0] bg-white py-20 text-center">
                <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#f8ecd7] to-[#efdcbd] text-[#b28585] ring-1 ring-[#e0b65c]/40">
                  <SearchOffRoundedIcon sx={{ fontSize: 38 }} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#511d00]">
                  No products found
                </h3>
                <p className="mt-1 max-w-sm text-sm text-[#8a7575]">
                  We couldn't find anything in this collection yet. Explore our
                  other royal wedding pieces.
                </p>
                <Button
                  variant="contained"
                  onClick={() => navigate("/")}
                  sx={{ mt: 3, borderRadius: "999px", px: 4 }}
                >
                  Back to Home
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 xl:grid-cols-4">
                {products.map((item) => (
                  <HomeProductCard
                    key={item._id}
                    product={item}
                    category={query}
                    view_all={view_all}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {products.length > 0 && totalPages > 1 && (
          <div className="flex justify-center py-10">
            <Pagination
              variant="outlined"
              onChange={handlePaginationChange}
              page={selectedPageNo}
              count={totalPages}
              renderItem={(item) => (
                <PaginationItem
                  slots={{
                    previous: ArrowBackIosNewRoundedIcon,
                    next: ArrowForwardIosRoundedIcon,
                  }}
                  {...item}
                  sx={{
                    "&.Mui-selected": {
                      background: "linear-gradient(135deg,#b28585,#5e3e3e)",
                      color: "#fff",
                      borderColor: "transparent",
                    },
                  }}
                />
              )}
            />
          </div>
        )}
      </main>

    </div>
  );
}
